import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDateShort, formatTime } from "@/lib/date";
import { ATID } from "@/lib/adminTestIds";
import {
  Newspaper, FileText, Clock, CalendarClock, Archive, Trash2,
  FolderKanban, PlusSquare, Eye, ArrowRight,
} from "lucide-react";

const STATUS_LABEL = {
  draft: "Borrador",
  pending: "Pendiente",
  scheduled: "Programada",
  published: "Publicada",
  archived: "Archivada",
  trash: "Papelera",
};

const STATUS_COLOR = {
  draft: "bg-lightgray text-textgray",
  pending: "bg-yellow-100 text-yellow-800",
  scheduled: "bg-blue-100 text-blue-800",
  published: "bg-watergreen/20 text-nightblue",
  archived: "bg-softblue/40 text-nightblue",
  trash: "bg-red-100 text-red-700",
};

function StatCard({ testId, label, value, icon: Icon, color = "text-petroleum", to }) {
  const inner = (
    <div className="bg-white border border-softblue p-5 h-full transition-colors hover:border-turquoise">
      <div className="flex items-center justify-between">
        <div className={`inline-flex items-center justify-center h-10 w-10 ${color} bg-lightgray`}>
          <Icon className="w-5 h-5" />
        </div>
        {to && <ArrowRight className="w-4 h-4 text-textgray" />}
      </div>
      <div className="mt-4 font-serif text-4xl font-black text-blueblack">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-widest text-textgray font-semibold">{label}</div>
    </div>
  );
  return (
    <div data-testid={testId}>
      {to ? <Link to={to}>{inner}</Link> : inner}
    </div>
  );
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.adminStats()
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid={ATID.dashboard}>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-semibold">Panel</div>
          <h1 className="font-serif font-black text-3xl sm:text-4xl text-blueblack">Inicio</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/admin/noticias/nueva"
            data-testid={ATID.quickCreateArticle}
            className="inline-flex items-center gap-2 bg-nightblue hover:bg-blueblack text-white h-10 px-4 text-xs font-semibold uppercase tracking-widest transition-colors"
          >
            <PlusSquare className="w-4 h-4" /> Nueva noticia
          </Link>
          <Link
            to="/admin/categorias"
            data-testid={ATID.quickCreateCategory}
            className="inline-flex items-center gap-2 border border-softblue hover:bg-lightgray text-nightblue h-10 px-4 text-xs font-semibold uppercase tracking-widest transition-colors"
          >
            <FolderKanban className="w-4 h-4" /> Nueva categoría
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 animate-pulse">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 bg-softblue/40" />
          ))}
        </div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard testId={ATID.statCard("published")} label="Publicadas" value={stats.published} icon={Newspaper} color="text-petroleum" to="/admin/noticias?status=published" />
            <StatCard testId={ATID.statCard("drafts")} label="Borradores" value={stats.drafts} icon={FileText} color="text-textgray" to="/admin/noticias?status=draft" />
            <StatCard testId={ATID.statCard("pending")} label="Pendientes" value={stats.pending} icon={Clock} color="text-yellow-700" to="/admin/noticias?status=pending" />
            <StatCard testId={ATID.statCard("scheduled")} label="Programadas" value={stats.scheduled} icon={CalendarClock} color="text-turquoise" to="/admin/noticias?status=scheduled" />
            <StatCard testId={ATID.statCard("archived")} label="Archivadas" value={stats.archived} icon={Archive} color="text-nightblue" to="/admin/noticias?status=archived" />
            <StatCard testId={ATID.statCard("categories")} label="Categorías" value={stats.categories} icon={FolderKanban} color="text-watergreen" to="/admin/categorias" />
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/admin/portada"
              className="inline-flex items-center gap-2 border border-softblue text-nightblue hover:bg-lightgray h-10 px-4 text-xs font-semibold uppercase tracking-widest transition-colors"
            >
              <Newspaper className="w-4 h-4" /> Ir al gestor de portada
            </Link>
            <Link
              to="/admin/multimedia"
              className="inline-flex items-center gap-2 border border-softblue text-nightblue hover:bg-lightgray h-10 px-4 text-xs font-semibold uppercase tracking-widest transition-colors"
            >
              Multimedia
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
            <div className="lg:col-span-2 bg-white border border-softblue">
              <div className="flex items-center justify-between px-5 py-4 border-b border-softblue">
                <h2 className="font-serif text-xl font-bold text-blueblack">Últimas actualizaciones</h2>
                <Link to="/admin/noticias" className="text-xs uppercase tracking-widest text-petroleum hover:text-turquoise font-semibold">
                  Ver todas →
                </Link>
              </div>
              {stats.latest.length === 0 ? (
                <div className="p-6 text-sm text-textgray">Todavía no hay noticias.</div>
              ) : (
                <ul>
                  {stats.latest.map((a) => (
                    <li key={a.id} className="flex items-center gap-4 px-5 py-3 border-b border-softblue last:border-b-0">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 font-semibold ${STATUS_COLOR[a.status] || "bg-lightgray"}`}>
                        {STATUS_LABEL[a.status] || a.status}
                      </span>
                      <div className="flex-1 min-w-0">
                        <Link to={`/admin/noticias/${a.id}`} className="font-medium text-blueblack hover:text-petroleum line-clamp-1">
                          {a.title}
                        </Link>
                        <div className="text-xs text-textgray mt-0.5">
                          {a.main_category_name} · {a.author_name}
                        </div>
                      </div>
                      <span className="text-xs text-textgray whitespace-nowrap">
                        {formatDateShort(a.updated_at)} · {formatTime(a.updated_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-nightblue text-white p-6">
              <div className="text-xs uppercase tracking-[0.18em] text-watergreen font-semibold">Resumen</div>
              <h2 className="font-serif text-2xl font-bold mt-2">Métricas rápidas</h2>
              <dl className="mt-6 space-y-4 text-sm">
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <dt className="text-white/70">Total de noticias</dt>
                  <dd className="font-serif font-black text-2xl">{stats.total_articles}</dd>
                </div>
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <dt className="text-white/70">Vistas publicadas</dt>
                  <dd className="font-serif font-black text-2xl">{stats.views_total.toLocaleString("es-AR")}</dd>
                </div>
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <dt className="text-white/70">Autores</dt>
                  <dd className="font-serif font-black text-2xl">{stats.authors}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-white/70">Papelera</dt>
                  <dd className="font-serif font-black text-2xl">{stats.trash}</dd>
                </div>
              </dl>
              <div className="mt-6 pt-4 border-t border-white/10 text-xs text-white/60 flex items-center gap-2">
                <Eye className="w-3.5 h-3.5" />
                <a href="/" target="_blank" rel="noopener noreferrer" className="hover:text-watergreen">
                  Abrir portal público
                </a>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-sm text-textgray">No se pudieron cargar las estadísticas.</div>
      )}
    </div>
  );
}
